import { useState, useEffect, useRef, useCallback } from 'react';
import { ChromeTokens } from './tokens';
import { Icon } from './Icon';

export type AvailabilityStatus = 'unchanged' | 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

export interface NameFieldProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Optional async availability check. Returns either a boolean
   * (`true` = available) or `{ available, reason? }` for richer feedback.
   * When omitted, the field renders as a plain labeled input with no status row.
   */
  checkAvailability?: (
    name: string,
  ) => Promise<boolean | { available: boolean; reason?: string }>;
  /**
   * Baseline value the typed input is compared against. When the trimmed
   * `value` equals `savedValue`, no availability call is made and the status
   * row is rendered as an invisible spacer (status = "unchanged").
   *
   * Per Req 21.3: when the candidate equals the current saved value, the
   * NameField shows no status indicator. Callers gate Save on
   * `status === "available"`, which never fires while the value is unchanged.
   */
  savedValue?: string;
  /** Fires whenever the displayed status changes — for Save-button gating. */
  onAvailabilityChange?: (status: AvailabilityStatus) => void;
  label?: string;
  placeholder?: string;
  minLength?: number;
  debounceMs?: number;
  disabled?: boolean;
}

/**
 * NameField — chrome primitive that pairs a labeled text input with a
 * debounced availability indicator.
 *
 * - Compares the trimmed `value` to `savedValue`. When equal, status is
 *   "unchanged" and the status row is an invisible spacer (Req 21.3).
 * - Otherwise debounces typing (default 600ms — see Req 21.2) and calls
 *   `checkAvailability(value.trim())`. While the call is in flight,
 *   status = "checking". When it resolves, status = "available" or "taken".
 * - Status row keeps a `min-height` of 18px so the layout does not jump
 *   between states (matches `rc_panels2.jsx`).
 * - A monotonic counter ignores stale promise resolutions, so a slow
 *   earlier call cannot overwrite a status produced by a more recent call
 *   (Req 21.4 / Property 13).
 *
 * Pure JS values produced by the prop callbacks: ChromeTokens for all
 * colors / radii / fonts — no inline hex / px / font-family literals.
 *
 * **Validates: Requirements 21.1, 21.2, 21.3, 21.4, 21.5**
 */
export function NameField({
  value,
  onChange,
  checkAvailability,
  savedValue,
  onAvailabilityChange,
  label,
  placeholder = 'Pick a name',
  minLength = 3,
  debounceMs = 600,
  disabled,
}: NameFieldProps) {
  const [status, setStatus] = useState<AvailabilityStatus>('unchanged');
  const [reason, setReason] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest-call-wins guard: every call increments the counter; a resolution
  // with a stale id is dropped.
  const callCounterRef = useRef(0);

  // Notify parent of every status transition (so the Save button can gate).
  useEffect(() => {
    onAvailabilityChange?.(status);
  }, [status, onAvailabilityChange]);

  const trimmed = value.trim();
  const baselineTrimmed = (savedValue ?? '').trim();
  const isUnchanged = trimmed === baselineTrimmed;

  const check = useCallback(
    async (name: string) => {
      if (!checkAvailability) {
        setStatus('idle');
        return;
      }
      const callId = ++callCounterRef.current;
      setStatus('checking');
      try {
        const res = await checkAvailability(name);
        if (callId !== callCounterRef.current) return; // stale — ignore
        const available = typeof res === 'boolean' ? res : res.available;
        const r = typeof res === 'object' ? res.reason : undefined;
        if (available) {
          setStatus('available');
          setReason('');
        } else {
          setStatus(r === 'invalid_format' ? 'invalid' : 'taken');
          setReason(r === 'invalid_format' ? 'Invalid format' : 'Already taken');
        }
      } catch {
        if (callId === callCounterRef.current) setStatus('idle');
      }
    },
    [checkAvailability],
  );

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    // Bail out early when the value matches the saved baseline (Req 21.3).
    // No availability call is made; status is `unchanged` so the Save button
    // stays disabled and the status row renders as an empty spacer.
    if (savedValue !== undefined && isUnchanged) {
      setStatus('unchanged');
      // Invalidate any in-flight call so a late resolution can't transition
      // the status away from 'unchanged'.
      callCounterRef.current++;
      return;
    }

    // Skip availability check when the value is too short to be meaningful.
    if (trimmed.length < minLength) {
      setStatus('idle');
      callCounterRef.current++;
      return;
    }

    timerRef.current = setTimeout(() => check(trimmed), debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [trimmed, savedValue, isUnchanged, minLength, debounceMs, check]);

  const borderColor =
    status === 'taken' || status === 'invalid'
      ? ChromeTokens.color.danger
      : status === 'available'
        ? ChromeTokens.color.grass
        : ChromeTokens.color.baseMuted;

  return (
    <div>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: ChromeTokens.color.ink,
            marginBottom: ChromeTokens.spacing.xs,
          }}
        >
          {label}
        </label>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="username"
        disabled={disabled}
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: 15,
          border: `1.5px solid ${borderColor}`,
          borderRadius: ChromeTokens.radius.md,
          background: ChromeTokens.color.base,
          color: ChromeTokens.color.ink,
          outline: 'none',
          fontFamily: ChromeTokens.font.ui,
          opacity: disabled ? 0.6 : 1,
          boxSizing: 'border-box',
        }}
      />
      {/* Status row — fixed 18px min-height so the layout never jumps as the
          status transitions between states (Req 21.5 / matches rc_panels2.jsx). */}
      <div
        role="status"
        aria-live="polite"
        style={{
          marginTop: ChromeTokens.spacing.xs,
          minHeight: 18,
          fontSize: 12.5,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          fontFamily: ChromeTokens.font.ui,
        }}
      >
        {status === 'checking' && (
          <span style={{ color: ChromeTokens.color.inkMuted }}>Checking…</span>
        )}
        {status === 'available' && (
          <>
            <Icon name="check" size={14} stroke={ChromeTokens.color.grass} />
            <span style={{ color: ChromeTokens.color.grass }}>
              &quot;{trimmed}&quot; is available
            </span>
          </>
        )}
        {(status === 'taken' || status === 'invalid') && (
          <>
            <Icon name="x" size={14} stroke={ChromeTokens.color.danger} />
            <span style={{ color: ChromeTokens.color.danger }}>
              &quot;{trimmed}&quot;{' '}
              {reason === 'Invalid format' ? 'is not valid' : 'is taken'}
            </span>
          </>
        )}
        {/* unchanged / idle render as invisible spacer to preserve min-height */}
      </div>
    </div>
  );
}
